import { createBuilder } from '@sama/js-core';
import {
  Point2D,
  Point3D,
} from '../../../team-apis/otterdam/answers/shape-typings';
import {
  AccumulatedGroundAttributes,
  CloudAttributes,
} from '../../utils/kd-tree/cloud-attributes.service';
import {
  buildKdTreeCloud,
  cloudPoint,
  groundPoint,
} from './build-kd-tree-cloud';
import {
  planeEquationGenerator,
  SurfaceFunction,
} from './point-cloud-buffer-attributes-equations-generators';

export interface CloudAttributeOptions {
  groundZFunc: SurfaceFunction;
  cloudZFunc: SurfaceFunction;
  totalGroundPoints: number;
  totalCloudPoints: number;
  minPoint: Point2D;
  maxPoint: Point2D;
}

interface AccumulatedGroundAttributeOptions {
  groundZFunc: SurfaceFunction;
  totalGroundPoints: number;
  minPoint: Point2D;
  maxPoint: Point2D;
}

export const buildCloudAttributes = createBuilder<
  CloudAttributes,
  CloudAttributeOptions
>((_, options) => {
  const groundPositions = buildPositions(
    options.minPoint,
    options.maxPoint,
    options.totalGroundPoints,
    options.groundZFunc,
  );
  const cloudPositions = buildPositions(
    options.minPoint,
    options.maxPoint,
    options.totalCloudPoints,
    options.cloudZFunc,
  );

  const cloudPoints = cloudPositions.map(cloudPoint);
  const groundPoints = groundPositions.map(groundPoint);

  return buildKdTreeCloud([...cloudPoints, ...groundPoints]);
});

export const buildAccumulatedGroundAttributes = createBuilder<
  AccumulatedGroundAttributes,
  AccumulatedGroundAttributeOptions
>((_, options) => {
  const groundPoints = buildPositions(
    options.minPoint,
    options.maxPoint,
    options.totalGroundPoints,
    options.groundZFunc,
  ).map(groundPoint);

  return buildKdTreeCloud(groundPoints);
});

export const defaultMinPoint: Point2D = [-200, -200];
export const defaultMaxPoint: Point2D = [200, 200];
export const defaultTotalPoints = 2000;

export function buildPositions(
  minPoint = defaultMinPoint,
  maxPoint = defaultMaxPoint,
  totalPoints = defaultTotalPoints,
  zFunc = planeEquationGenerator(),
) {
  const distanceBetweenPoints =
    (Math.sqrt(maxPoint[0] - minPoint[0]) *
      Math.sqrt(maxPoint[1] - minPoint[1])) /
    Math.sqrt(totalPoints);

  const positions: Point3D[] = [];

  // pointsPerRow = total number of world units per row * # of points in one
  // world unit
  const pointsPerRow = (maxPoint[1] - minPoint[1]) / distanceBetweenPoints;

  for (let i = 0; i < totalPoints; i++) {
    const currentRow = Math.floor(i / pointsPerRow);
    const x =
      ((distanceBetweenPoints * i) % (pointsPerRow * distanceBetweenPoints)) +
      minPoint[0];
    const y = currentRow * distanceBetweenPoints + minPoint[1];

    const z = zFunc(x, y);

    positions[i] = [x, y, z];
  }

  return positions;
}
